import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useEffect, useState } from "react";

interface BreadcrumbItem {
  label: string;
  path: string;
}

export function Breadcrumbs() {
  const location = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const crumbs: BreadcrumbItem[] = [{ label: "Home", path: "/" }];

    let currentPath = "";
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Capitalize and format segment
      let label = segment
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      // Special cases for better label readability
      const labelMap: Record<string, string> = {
        "blog": "Blog",
        "seven-signs": "7 Signs Guide",
        "7-signs": "7 Signs Guide",
        "nice-guy-assessment": "Nice Guy Assessment",
        "submit-testimonial": "Submit Testimonial",
        "download-success": "Download Complete",
        "newsletter-archive": "Newsletter Archive",
        "forgot-password": "Forgot Password",
        "reset-password": "Reset Password",
        "profile-settings": "Profile Settings",
        "about": "About",
        "resources": "Resources",
        "contact": "Contact",
        "auth": "Sign In",
        "unsubscribe": "Unsubscribe",
      };
      
      if (labelMap[segment]) {
        label = labelMap[segment];
      }

      crumbs.push({ label, path: currentPath });
    });

    setBreadcrumbs(crumbs);
  }, [location.pathname]);

  if (breadcrumbs.length <= 1) return null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.label,
      "item": `https://souldadrob.com${crumb.path}`
    }))
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <nav aria-label="Breadcrumb" className="container mx-auto px-4 py-4">
        <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
          {breadcrumbs.map((crumb, index) => (
            <li key={crumb.path} className="flex items-center">
              {index > 0 && <ChevronRight className="h-4 w-4 mx-2" />}
              {index === breadcrumbs.length - 1 ? (
                <span className="text-foreground font-medium flex items-center">
                  {index === 0 && <Home className="h-4 w-4 mr-1" />}
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className="hover:text-foreground transition-colors flex items-center"
                >
                  {index === 0 && <Home className="h-4 w-4 mr-1" />}
                  {crumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}
